import { isEmpty } from "lodash-es";
import { Alert, Button, DeleteIcon, Space } from "melco-ui";
import { useTranslation } from "react-i18next";
import { Key } from "antd/lib/table/interface";
import { useDeleteDesignCollections } from "../../../hooks/libraries/designCollections/useDeleteDesignCollections";
import { SharedserviceMelcoModelsDesignCollection, useConfirm } from "melco-shared-logic";
import { useShowWarningForRowKeys } from "../../../hooks/table/useShowWarningForRowKeys";

type BulkActionDeleteDesignCollectionsProps<T> = {
  selectedRowKeys: Key[];
  setSelectedRowKeys: (keys: Key[]) => void;
  refetch?: () => Promise<any>;
  data?: T[];
};

export const BulkActionDeleteDesignCollections: React.FC<
  BulkActionDeleteDesignCollectionsProps<SharedserviceMelcoModelsDesignCollection>
> = ({ selectedRowKeys, setSelectedRowKeys, refetch, data }) => {
  const { t } = useTranslation();

  const deleteDesignCollections = useDeleteDesignCollections();
  const showWarningForRowKeys = useShowWarningForRowKeys(selectedRowKeys, data);

  const confirmDelete = useConfirm(
    async () => {
      const successfullyDeletedIds = await deleteDesignCollections(
        selectedRowKeys.map((key) => key.toString())
      );

      if (refetch) {
        refetch();
      }

      // keep items that were not deleted selected
      setSelectedRowKeys(
        selectedRowKeys.filter(
          (id) => !successfullyDeletedIds.includes(id.toString())
        )
      );
    },
    {
      translationPrefix: "libraries.design_collections.actions.delete",
      translationOptions: {
        count: selectedRowKeys.length,
      },
      content: showWarningForRowKeys.length && (
        <Space direction="vertical" size="small">
          {t(
            "libraries.design_collections.actions.delete.confirmation.message",
            {
              count: selectedRowKeys.length,
            }
          )}
          <Alert
            type="warning"
            withBackground
            description={t(
              "libraries.design_collections.actions.delete.confirmation.warning",
              {
                count: showWarningForRowKeys.length,
              }
            )}
            showIcon={false}
          />
        </Space>
      ),
    }
  );

  if (isEmpty(selectedRowKeys)) {
    return null;
  }

  return (
    <Button
      key="bulk-delete"
      icon={<DeleteIcon />}
      onClick={() => {
        confirmDelete();
      }}
    >
      {t("libraries.design_collections.actions.delete.button")}
    </Button>
  );
};
